// WheelBarrelOccluder — port of the legacy WebAR catalog techniques
// 3.1 (depth-only mask cylinder) + 3.3 (black flat-shader backdrop
// disc). Reference source: `docs/8th-wall/wheelobject.js:162-174`
// (cylinder) + `220-232` (backdrop) + `687-701` (colorWrite=false
// material). Ported verbatim in geometry / positioning; A-Frame
// primitives translated to their THREE.js equivalents.
//
// Two-piece construction:
//
//   1. Open-ended CYLINDER around the wheel barrel with `colorWrite =
//      false`. Writes to the DEPTH buffer but NOT the color buffer,
//      so anything rendered behind it (further from the camera) is
//      hidden by depth-test, while the cylinder itself is invisible.
//      Sized slightly larger than the face rim so it fully covers the
//      barrel from every viewing angle.
//
//   2. Solid black CIRCLE just behind the wheel's back face. Renders
//      with a flat shader (no lighting), so gap pixels through the
//      spokes see straight through to a matte black surface — the
//      look of a wheel with a solid disc-brake / dark barrel behind
//      it. Placed just past `modelSize.z` from the face plane.
//
// Wheel-local axes (matches the renderer's applyTransform assumption):
//   +Z = out of the wheel face toward the viewer
//   -Z = into the barrel
//
// Both pieces are added to a group returned by this factory; the
// caller adds that group as a child of the placed wheel so it inherits
// every subsequent transform (position, orientation, scale, per-frame
// anchor sync).

import * as THREE from 'three'

/** Cylinder height as a multiple of the face diameter. Legacy hard-
 *  codes 0.78 m for a ~0.6 m-diameter wheel — same 1.3× ratio. Enough
 *  to hide the barrel from every viewing angle at typical distances. */
const CYLINDER_HEIGHT_TO_DIAMETER = 1.3

/** Slight radial padding on the mask cylinder so it fully brackets the
 *  wheel rim's outer edge (legacy uses +0.002 m). */
const CYLINDER_RADIUS_PADDING_M = 0.002

/** Backdrop disc radius relative to face radius. Legacy 0.99× — the
 *  disc's rim is JUST inside the wheel rim, so the wheel body itself
 *  occludes any sliver of the disc's edge from every angle. */
const BACKDROP_RADIUS_RATIO = 0.99

/** How far forward from the wheel's BACK PLANE the backdrop sits. Legacy
 *  `z = -modelSize.z + 0.04`: 4 cm in from the back. Recessing this deep
 *  (rather than near the face) is what gives the wheel interior its
 *  "dark barrel" look — you're seeing INTO the barrel through the
 *  spokes, not at a flat card 4cm behind the face. */
const BACKDROP_INSET_FROM_BACK_M = 0.04

/** Clearance behind the rearmost brake geometry, so the backdrop disc
 *  never cuts through the caliper it is meant to sit behind. */
const BACKDROP_BEHIND_BRAKE_CLEARANCE_M = 0.01

/** part_type_id of the brake, as the assembler names it — every loaded
 *  part becomes a node called `PART_<part_type_id>` (assembler.ts). Same
 *  id the app uses when it gates brake visibility. */
const BRAKE_PART_NODE = 'PART_2'

/** The authored mounting-pad mesh. Carried as both the mesh name and its
 *  material name; the app measures backspace off this same datum
 *  (ADR-077 — the backpad empty is the assembly datum). */
const BACKPAD_NAME = 'BACKPAD'

/** Tire ring — a soft blend outside the rim. Not from legacy (8th-wall
 *  code has no gradient); modelled after real-world context: a mounted
 *  wheel has a black tire around it, and its silhouette blurs into that
 *  tire. Outer radius was 1.15× face radius; the gradient SPREAD (the
 *  0.15× band beyond the rim) was tightened 10% on 2026-08-21 field
 *  feedback → 0.135× band, i.e. 1.135× outer. */
const TIRE_RING_OUTER_RATIO = 1.135

/** Peak opacity at the ring's inner (rim) edge. Full black read as a
 *  hard collar against real tires; 75% lets the camera feed through
 *  and blends far better (2026-08-21). */
const TIRE_RING_MAX_ALPHA = 0.75

/** The ring's colour, as an sRGB fraction (2026-09-16).
 *
 *  It was pure black, and no tire is pure black — a sidewall in daylight
 *  measures around sRGB #1f1f1f to #2e2e2e, never #000000. Against a real
 *  tire the black ring read as a hole rather than as rubber.
 *
 *  0.149 is #262626. The value is in sRGB units, NOT linear: this is a raw
 *  ShaderMaterial with no `colorspace_fragment` include, so what the shader
 *  writes is what reaches the framebuffer. Passing a THREE.Color here would
 *  be converted to working space and land about two stops too dark. */
const TIRE_RING_SRGB = 0.149

/** How far BEHIND the wheel face plane the ring sits. The ring's
 *  opaque overlap band is coplanar with the wheel's own rim geometry
 *  at z=0, so depth-test ties resolved per-pixel per-frame and the
 *  seam shimmered — the field-reported "vibrating halo" (2026-08-21).
 *  Recessing 3mm makes the wheel body win the depth test outright and
 *  hides the overlap band behind the rim; at arm's length the parallax
 *  from 3mm is sub-pixel. */
const TIRE_RING_Z_RECESS_M = 0.003

/** Radial overlap of the ring's INNER edge INTO the wheel rim — the
 *  ring's geometry starts 0.01 m INSIDE the rim so there's never a
 *  hairline gap between the wheel's outer edge and the ring's opaque
 *  start. Alpha stays fully opaque across the overlap and only begins
 *  to fade once the geometry crosses the wheel rim. */
const TIRE_RING_INNER_OVERLAP_M = 0.01

/** Builds the barrel occluder sized for a wheel with the given face
 *  radius and width. Returns a group to be added as a child of the
 *  placed wheel; sits in wheel-local space, so both pieces move / scale
 *  / rotate with the wheel body. */
export function createWheelBarrelOccluder(
  faceRadiusM: number,
  widthM: number,
  /** ?depthPrepass — rebuild every wheel's depth after the per-wheel
   *  passes, so the transparent gradient can depth-test against the
   *  whole scene and sit behind a nearer wheel like the body does.
   *
   *  The mask cylinder STAYS. Removing it exposed the entire barrel
   *  (2026-08-23 LUAT) — it hides the barrel walls, not just the back,
   *  and at 2mm of radial padding it was never the oversized culprit.
   *  What breaks the gradient is the depth CLEARING between wheels, and
   *  the answer to that is to restore the depth afterwards rather than
   *  to delete the occluder. */
  depthPrepass = false,
  /** Occluder-local Z for the backdrop disc, from deriveWheelDimensions.
   *  Omitted → the legacy width-derived inset. */
  backdropZM?: number,
): THREE.Object3D {
  const group = new THREE.Group()
  group.name = 'wheel-barrel-occluder'

  group.add(buildMaskCylinder(faceRadiusM))
  group.add(buildBackdropDisc(faceRadiusM, widthM, backdropZM))
  // ?depthPrepass: a second depth-only stamp of the same cylinder,
  // rendered at the very END of the opaque queue. The per-wheel passes
  // clear depth as they go, so by the end the buffer holds only the
  // last wheel — and the transparent gradient had nothing truthful to
  // test against. This rebuilds every wheel's depth after all of them
  // have drawn, which is the pre-pass the gradient needs.
  if (depthPrepass) group.add(buildDepthRestamp(faceRadiusM))
  group.add(buildTireLipRing(faceRadiusM, depthPrepass))

  return group
}

/** Depth-only stamp of the barrel cylinder, drawn after every wheel has
 *  had its isolated pass. Invisible; its whole job is to leave the full
 *  scene depth in the buffer for the transparent phase that follows. */
function buildDepthRestamp(faceRadiusM: number): THREE.Mesh {
  const mesh = buildMaskCylinder(faceRadiusM)
  mesh.name = 'barrel-depth-restamp'
  return mesh
}

/** Legacy technique 3.1 — open-ended cylinder with `colorWrite = false`.
 *  Depth is written, color is not. The wheel body renders after (higher
 *  renderOrder) with its opaque spokes; any pixel a would-be-visible
 *  scene object might have covered behind the cylinder is depth-culled
 *  instead. */
function buildMaskCylinder(faceRadiusM: number): THREE.Mesh {
  const height = faceRadiusM * 2 * CYLINDER_HEIGHT_TO_DIAMETER
  const geometry = new THREE.CylinderGeometry(
    faceRadiusM + CYLINDER_RADIUS_PADDING_M,
    faceRadiusM + CYLINDER_RADIUS_PADDING_M,
    height,
    64,
    1,
    true, // openEnded — legacy `open-ended: true`
  )
  // Three's CylinderGeometry axis defaults to +Y. Rotate 90° about X
  // so the axis lies on +Z (wheel rotational axis in our frame).
  geometry.rotateX(Math.PI / 2)

  const material = new THREE.MeshBasicMaterial()
  material.colorWrite = false
  material.needsUpdate = true

  const mesh = new THREE.Mesh(geometry, material)
  mesh.name = 'barrel-mask-cylinder'
  // Legacy positions at z=-0.4 with height=0.78 → front cap at z ≈ -0.01
  // (essentially at the wheel face plane), back cap at z ≈ -0.79 (deep
  // in the well). Same shape here: center at -height/2 → front cap at 0.
  mesh.position.z = -height / 2
  // Render BEFORE the wheel body so depth is stamped in time to hide
  // whatever comes after.
  mesh.renderOrder = -1
  return mesh
}

/** Legacy technique 3.3 — flat black circle recessed near the wheel's
 *  BACK plane (not near the face). Gap pixels between spokes see
 *  through into a deep, dark barrel — that recession is what gives
 *  the wheel interior its "solid metal disc-brake" look; a shallow
 *  card near the face reads as a flat 2D cutout. Legacy position:
 *  `z = -modelSize.z + 0.04`. Ported verbatim. Radius 0.99× so the
 *  wheel rim itself occludes any sliver of the disc's edge from every
 *  viewing angle → parallax is invisible. Flat black, no gradient
 *  (that's on the tire ring). */
function buildBackdropDisc(
  faceRadiusM: number,
  widthM: number,
  backdropZM?: number,
): THREE.Mesh {
  const geometry = new THREE.CircleGeometry(
    faceRadiusM * BACKDROP_RADIUS_RATIO,
    64,
  )
  const material = new THREE.MeshBasicMaterial({
    color: 0x000000,
    side: THREE.DoubleSide,
  })
  const mesh = new THREE.Mesh(geometry, material)
  mesh.name = 'barrel-backdrop-disc'
  // Placement comes from deriveWheelDimensions, which anchors the disc to
  // real geometry — the authored backpad, or behind the brake when one is
  // in the scene. The width-derived inset below is the fallback for a
  // template carrying neither datum; it was the only rule until 2026-09-04,
  // and on a brake-bearing deep dish it put the disc INSIDE the brake.
  mesh.position.z = backdropZM ?? -(widthM - BACKDROP_INSET_FROM_BACK_M)
  return mesh
}

/** Soft "tire" ring at the wheel face plane, outside the rim. Alpha
 *  ramps to its peak at the inner edge (rim) → transparent at the
 *  outer edge (1.15× face radius). Faked tire silhouette that hides
 *  the hard cut-out look of a floating wheel rim in AR. Not from the
 *  legacy — user-requested addition. */
function buildTireLipRing(faceRadiusM: number, depthPrepass: boolean): THREE.Mesh {
  // Geometry inner edge sits INSIDE the wheel rim by TIRE_RING_INNER_
  // OVERLAP_M so there's never a hairline gap between the metal edge
  // and the ring's opaque start. Alpha stays 1 across the overlap and
  // only starts fading once the fragment's radial position crosses
  // the wheel rim (r = faceRadius).
  const innerRadius = faceRadiusM - TIRE_RING_INNER_OVERLAP_M
  const outerRadius = faceRadiusM * TIRE_RING_OUTER_RATIO
  const geometry = new THREE.RingGeometry(innerRadius, outerRadius, 64, 1)
  // RingGeometry UV = (vertex.xy / outerRadius + 1) / 2 → distance from
  // (0.5, 0.5) = r / outerRadius / 2 for a vertex at radius r. So the
  // wheel rim (r = faceRadiusM) sits at UV distance faceRadius/outer/2;
  // the ring's outer edge sits at 0.5.
  const uOverlapEndR = faceRadiusM / outerRadius / 2
  const uOuterR = 0.5
  const material = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    // With the real depth of every wheel in the buffer (?depthPrepass),
    // the gradient can behave like the geometry it belongs to: hidden
    // behind a nearer wheel, and clipped by its own rim. Without the
    // flag the buffer is per-wheel isolated, so testing against it
    // produced the wrong answer and the ring draws unoccluded instead.
    depthTest: depthPrepass,
    // Legacy path (flag off): NOT depth-tested — a deliberate trade-off
    // rather than an oversight.
    //
    // The gradient is transparent, and three renders transparent objects
    // in a phase that runs after EVERY opaque object — structurally
    // outside its own wheel's depth pass. Two attempts to drag it back
    // in both regressed (2026-08-23): re-stamping depth WITH a clear let
    // a further gradient paint over a nearer wheel; WITHOUT a clear it
    // tested against the nearest wheel's oversized barrel mask and got
    // bitten. Moving it to the opaque queue rendered it as a hard disc,
    // because the queue flag also governs blending in practice.
    //
    // So it draws unoccluded, layered far-to-near by renderOrder. The
    // known cost: a further wheel's gradient is not hidden behind a
    // nearer wheel. Fixing that properly needs per-wheel stencil
    // masking, which is its own piece of work — not another guess.

    side: THREE.DoubleSide,
    uniforms: {
      uOverlapEndR: { value: uOverlapEndR },
      uOuterR: { value: uOuterR },
      uMaxAlpha: { value: TIRE_RING_MAX_ALPHA },
      uGray: { value: TIRE_RING_SRGB },
    },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      uniform float uOverlapEndR;
      uniform float uOuterR;
      uniform float uMaxAlpha;
      uniform float uGray;
      varying vec2 vUv;
      void main() {
        float r = distance(vUv, vec2(0.5));
        // Peaks at uMaxAlpha across the overlap band (r <
        // uOverlapEndR); ramps to 0 across uOverlapEndR..uOuterR
        // (wheel rim → ring's outer edge).
        float alpha = uMaxAlpha * (1.0 - smoothstep(uOverlapEndR, uOuterR, r));
        gl_FragColor = vec4(uGray, uGray, uGray, alpha);
      }
    `,
  })
  const mesh = new THREE.Mesh(geometry, material)
  mesh.name = 'tire-lip-ring'
  // Just BEHIND the face plane — see TIRE_RING_Z_RECESS_M. Coplanar
  // (z=0) z-fought the rim and shimmered.
  mesh.position.z = -TIRE_RING_Z_RECESS_M
  return mesh
}

/** ?use_ios_ui (2026-10-17) — the iOS MASK band in place of the soft
 *  proportional ring: fully opaque from the rim out to `solidM`, then a
 *  linear fade across `fadeM` (iOS ARService+CarModeMasking blend ring).
 *  Widths are in the occluder's OWN units; the renderer converts real
 *  metres before calling. Same name, z recess, colour and depth rules as
 *  buildTireLipRing, so render-order slotting treats the two alike. */
export function buildTireBandRing({
  faceRadiusM,
  solidM,
  fadeM,
  depthPrepass,
}: {
  faceRadiusM: number
  solidM: number
  fadeM: number
  depthPrepass: boolean
}): THREE.Mesh {
  const innerRadius = faceRadiusM - TIRE_RING_INNER_OVERLAP_M
  const solidRadius = faceRadiusM + solidM
  const outerRadius = solidRadius + fadeM
  const geometry = new THREE.RingGeometry(innerRadius, outerRadius, 64, 1)
  // Same UV convention as buildTireLipRing: r_uv = r / outerRadius / 2.
  const material = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    depthTest: depthPrepass,
    side: THREE.DoubleSide,
    uniforms: {
      uSolidR: { value: solidRadius / outerRadius / 2 },
      uOuterR: { value: 0.5 },
      uGray: { value: TIRE_RING_SRGB },
    },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      uniform float uSolidR;
      uniform float uOuterR;
      uniform float uGray;
      varying vec2 vUv;
      void main() {
        float r = distance(vUv, vec2(0.5));
        float alpha = 1.0 - clamp((r - uSolidR) / max(uOuterR - uSolidR, 1e-6), 0.0, 1.0);
        gl_FragColor = vec4(uGray, uGray, uGray, alpha);
      }
    `,
  })
  const mesh = new THREE.Mesh(geometry, material)
  mesh.name = 'tire-lip-ring'
  mesh.position.z = -TIRE_RING_Z_RECESS_M
  return mesh
}

/** Derives (faceRadius, width) from an already-scaled template group.
 *
 *  The face diameter is read off the FACE PLANE (local X/Y) and the width
 *  off the wheel's axis of revolution (local Z) — the same convention this
 *  module's header states and the renderer's applyTransform already relies
 *  on (`+Z = out of the wheel face toward the viewer`).
 *
 *  It previously sorted the three extents and took the smallest as width,
 *  the largest as face diameter, on the stated assumption that wheels are
 *  "much wider on the face plane than in the depth". That assumption fails
 *  on a wide wheel, and the failure is not marginal. Measured on the
 *  assembled Weld S70 15x14 that AR is actually served
 *  (`assembler.autoviz.io/file/16bhmma6c2.glb`): x 0.4157, y 0.4159,
 *  z 0.4478 — the model's LARGEST extent is its WIDTH. The brake's bleeder
 *  and fitting hang ~5.5cm off the back, and at 15x14 the width-to-diameter
 *  margin is only ~6%, so the brake alone tips the sort.
 *
 *  The consequence was a face radius 22% too large, and every consumer here
 *  takes faceRadiusM: the mask cylinder, the backdrop disc, the depth
 *  restamp and the tire lip ring. The field report was the ring — it drew
 *  20.01" across a 16.37" wheel (2026-09-04).
 *
 *  Deriving the axis was always redundant: the authoring contract fixes it
 *  on Z, and the caller already depends on that when it shifts the body by
 *  `-bodyBox.max.z` to put the face plane at the group origin. Reading the
 *  axis rather than guessing it is correct at every aspect ratio, including
 *  a wheel wider than it is tall.
 *
 *  NOTE (not fixed here, deliberately): the box still spans the WHOLE
 *  template, brake and lug nuts included, so `widthM` on a brake-bearing
 *  wheel is ~5.5cm too deep and the backdrop disc sits that much further
 *  back. The app excludes both from wheel bounds for exactly this reason
 *  (`isExcludedFromWheelBounds`). Changing it moves the "dark barrel" look,
 *  so it is its own change with its own UAT. */
export function deriveWheelDimensions(template: THREE.Object3D): {
  faceRadiusM: number
  widthM: number
  backdropZM: number
} {
  const box = new THREE.Box3().setFromObject(template)
  const size = box.getSize(new THREE.Vector3())
  const widthM = size.z
  const faceDiameterM = Math.max(size.x, size.y)
  return {
    faceRadiusM: faceDiameterM / 2,
    widthM,
    backdropZM: deriveBackdropZ(template, box.max.z, widthM),
  }
}

/** Finds the first descendant whose own name — or its material's — matches,
 *  case-insensitively. The assembler carries the authored name on both. */
function findByName(root: THREE.Object3D, name: string): THREE.Object3D | null {
  const want = name.toLowerCase()
  let hit: THREE.Object3D | null = null
  root.traverse((o) => {
    if (hit) return
    const material = (o as THREE.Mesh).material as THREE.Material | undefined
    const materialName = Array.isArray(material) ? '' : (material?.name ?? '')
    if (o.name.toLowerCase() === want || materialName.toLowerCase() === want) {
      hit = o
    }
  })
  return hit
}

/** Occluder-local Z for the backdrop disc.
 *
 *  The disc is what a viewer sees through the spokes, so it has to sit on a
 *  plane that physically exists rather than at an offset from the model's
 *  overall depth:
 *
 *  1. **Behind the brake, when a brake is in the scene.** The brake is the
 *     rearmost thing there, and the disc has to be behind it or it cuts
 *     straight through the caliper and rotor.
 *  2. **Aligned with the authored backpad otherwise.** That is the mounting
 *     plane — where the hub face would be on a fitted wheel — and it is the
 *     same datum ADR-077 measures backspace from.
 *  3. Failing both, the legacy width-derived inset.
 *
 *  The old rule was (3) alone, and on the assembled Weld S70 15x14 it put
 *  the disc at local -0.4078: the brake spans -0.4479 to -0.3023 there, so
 *  the disc landed between the rotor and the caliper. Deep dish is what
 *  exposes it — that wheel's LIP is the frontmost geometry by 0.27 m, so
 *  "0.04 m in from the back plane" bears no relation to any real surface.
 *
 *  `frontZ` is the template's frontmost point, which the caller puts at the
 *  occluder group's origin (`body.position.z = -bodyBox.max.z`), so every
 *  template-frame Z converts by subtracting it. */
function deriveBackdropZ(
  template: THREE.Object3D,
  frontZ: number,
  widthM: number,
): number {
  const brake = template.getObjectByName(BRAKE_PART_NODE)
  if (brake) {
    const brakeBox = new THREE.Box3().setFromObject(brake)
    if (Number.isFinite(brakeBox.min.z)) {
      return brakeBox.min.z - BACKDROP_BEHIND_BRAKE_CLEARANCE_M - frontZ
    }
  }

  const backpad = findByName(template, BACKPAD_NAME)
  if (backpad) {
    const padBox = new THREE.Box3().setFromObject(backpad)
    if (Number.isFinite(padBox.min.z)) {
      return (padBox.min.z + padBox.max.z) / 2 - frontZ
    }
  }

  return -(widthM - BACKDROP_INSET_FROM_BACK_M)
}
